import React, { createContext, useContext, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const api = useMemo(() => ({
    show: (message, type = 'info', ttl = 3000, opts = {}) => {
      const options = { autoClose: ttl, ...opts };
      switch (type) {
        case 'success': return toast.success(message, options);
        case 'error': return toast.error(message, options);
        case 'warn': return toast.warn(message, options);
        case 'info': default: return toast.info(message, options);
      }
    },
    success: (m, ttl, opts) => toast.success(m, { autoClose: ttl, ...opts }),
    error: (m, ttl, opts) => toast.error(m, { autoClose: ttl, ...opts }),
    info: (m, ttl, opts) => toast.info(m, { autoClose: ttl, ...opts }),
    warn: (m, ttl, opts) => toast.warn(m, { autoClose: ttl, ...opts }),
    remove: (id) => toast.dismiss(id),
    // Confirmation toast that returns a Promise<boolean>
    confirm: (message, options = {}) => {
      const { okText = 'Yes', cancelText = 'Cancel', type = 'warning', autoClose = false, ...rest } = options;
      return new Promise((resolve) => {
        const id = toast(
          ({ closeToast }) => (
            <div className="min-w-[260px]">
              <div className="mb-3 text-sm">{message}</div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { resolve(false); closeToast?.(); }}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                >{cancelText}</button>
                <button
                  onClick={() => { resolve(true); closeToast?.(); }}
                  className="px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700"
                >{okText}</button>
              </div>
            </div>
          ),
          { autoClose, closeOnClick: false, type, ...rest }
        );
        return id;
      });
    }
  }), []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer position="bottom-right" newestOnTop closeOnClick pauseOnHover theme="light" />
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
